import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../api.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Button, Field, inputClass } from '../components/ui.js';
import { completeOauth } from '../lib/auth.js';
import { setLastAuthMethod } from '../lib/lastAuth.js';

interface Parsed {
  error: string | null;
  token: string | null;
  status: string | null;
  grant: string | null;
  email: string | null;
  provider: string | null;
}

const ERROR_COPY: Record<string, string> = {
  work_email_required:
    'Please use your work email — personal addresses like Gmail or Yahoo aren’t accepted.',
  challenge_failed: 'That sign-in attempt could not be verified. Please try again.',
  oauth_failed: 'Sign-in with that provider didn’t complete. Please try again.',
};

/** Read the OAuth result the API put in the URL fragment. */
function parseHashParams(): Parsed {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return {
    error: p.get('error'),
    token: p.get('token'),
    status: p.get('status'),
    grant: p.get('grant'),
    email: p.get('email'),
    provider: p.get('provider'),
  };
}

/**
 * Lands the OAuth redirect. The API hands results back in the URL fragment:
 *  #token=…&status=…  → existing user (sign in, or show pending)
 *  #grant=…&email=…   → new user → finish-signup form (account name + terms)
 *  #error=…           → friendly error
 * The fragment is parsed ONCE (lazy useState, before anything strips it) so a
 * StrictMode remount / effect re-run can't re-read an emptied hash and mislabel
 * the result. We strip it from history separately, after capture.
 */
export function OAuthCallback() {
  const { baseUrl, signIn } = useAuth();
  const navigate = useNavigate();
  const [parsed] = useState<Parsed>(parseHashParams);
  const [accountName, setAccountName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const signedIn = useRef(false);

  // Scrub the token/grant from the URL + history (after the lazy parse captured it).
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  // Remember the provider so the login page can hint "Last used" next time. We got
  // here via Google/Microsoft, so record it whenever the provider is known.
  useEffect(() => {
    if (parsed.provider === 'google' || parsed.provider === 'microsoft') {
      setLastAuthMethod(parsed.provider);
    }
  }, [parsed]);

  // Existing user with an active account → sign in and go to the dashboard.
  useEffect(() => {
    if (signedIn.current) return;
    if (parsed.token && parsed.status === 'active') {
      signedIn.current = true;
      signIn(parsed.token);
      navigate('/');
    }
  }, [parsed, signIn, navigate]);

  const providerName = parsed.provider === 'microsoft' ? 'Microsoft' : 'Google';

  const finish = async () => {
    if (!parsed.grant) return;
    setBusy(true);
    setError(null);
    try {
      const res = await completeOauth(baseUrl, {
        grant: parsed.grant,
        accountName,
        acceptedTerms: true,
      });
      if (res.account && res.account.status === 'active') {
        signIn(res.token);
        navigate('/');
        return;
      }
      setPending(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not finish signing up');
    } finally {
      setBusy(false);
    }
  };

  // Existing active user — the effect above is signing them in.
  if (parsed.token && parsed.status === 'active') {
    return <AuthLayout title="Signing you in…">{null}</AuthLayout>;
  }

  // An operator disabled this account: say so explicitly (data is preserved).
  if (parsed.token && parsed.status === 'suspended') {
    return (
      <AuthLayout title="Account disabled">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          This account has been disabled by CommissionSight. Your data is preserved and nothing has
          been deleted — contact{' '}
          <a className="font-medium underline" href="mailto:support@commissionsight.com">
            support@commissionsight.com
          </a>{' '}
          to restore access.
        </div>
        <Link
          to="/login"
          className="mt-4 block text-center text-sm font-medium text-emerald-600 hover:underline"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  // New account created and awaiting approval, or an existing pending account.
  if (pending || (parsed.token && parsed.status !== 'active')) {
    return (
      <AuthLayout title="Account pending approval">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          Thanks for signing up. Your account is awaiting approval — we'll email you the moment
          access is granted.
        </div>
        <Link
          to="/login"
          className="mt-4 block text-center text-sm font-medium text-emerald-600 hover:underline"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (parsed.error) {
    return (
      <AuthLayout title="Sign-in didn’t complete">
        <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {ERROR_COPY[parsed.error] ?? 'Something went wrong. Please try again.'}
        </p>
        <Link
          to="/login"
          className="block text-center text-sm font-medium text-emerald-600 hover:underline"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  // New user: finish signup with a verified email.
  if (parsed.grant && parsed.email) {
    return (
      <AuthLayout
        title="Finish setting up your account"
        subtitle="Your email is verified — just name your account."
      >
        {error && (
          <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        )}
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (accountName && agreed) finish();
          }}
        >
          <Field label="Work email">
            <input className={`${inputClass} opacity-70`} value={parsed.email} readOnly disabled />
            <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
              Verified via {providerName} ✓
            </p>
          </Field>
          <Field label="Account name">
            <input
              className={inputClass}
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="Acme Brokerage"
            />
          </Field>
          <label className="flex items-start gap-2.5 text-sm text-neutral-600 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500/30 dark:border-neutral-600 dark:bg-neutral-900"
            />
            <span>
              I agree to the{' '}
              <a
                href="https://commissionsight.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-600 hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://commissionsight.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-600 hover:underline"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <Button
            type="submit"
            className="w-full"
            loading={busy}
            disabled={!accountName || !agreed}
          >
            {busy ? 'Creating…' : 'Create account'}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  // Nothing recognizable in the fragment.
  return (
    <AuthLayout title="Sign-in didn’t complete">
      <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-300">
        This sign-in link is invalid or has already been used.
      </p>
      <Link
        to="/login"
        className="block text-center text-sm font-medium text-emerald-600 hover:underline"
      >
        Back to sign in
      </Link>
    </AuthLayout>
  );
}
